/**
 * QR Code PDF Generator
 * Generates printable A4 PDFs with QR codes for public issue reporting
 *
 * Uses pdfmake for client-side PDF generation with built-in QR code support.
 * P1-3: Uses dynamic imports to avoid loading 1.9MB pdfmake in main bundle.
 */
import type { TDocumentDefinitions, Content, TCreatedPdf } from 'pdfmake/interfaces';

// Lazy-loaded pdfmake instance
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let pdfMakeInstance: any = null;

/**
 * Lazily load pdfmake and its fonts
 * P1-3: Dynamic import reduces initial bundle size by ~1.9MB
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getPdfMake = async (): Promise<any> => {
    if (pdfMakeInstance) {
        return pdfMakeInstance;
    }

    const [pdfMakeModule, pdfFontsModule] = await Promise.all([
        import('pdfmake/build/pdfmake'),
        import('pdfmake/build/vfs_fonts'),
    ]);

    // P2-8: Dynamic imports return ES modules - access .default for the actual exports
    // pdfmake types don't accurately reflect the runtime structure
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pdfMake = (pdfMakeModule as any).default || pdfMakeModule;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pdfFonts = (pdfFontsModule as any).default || pdfFontsModule;

    // Initialize fonts - pdfFonts may have vfs directly or nested under pdfMake
    pdfMake.vfs = pdfFonts.pdfMake?.vfs || pdfFonts.vfs || pdfFonts;

    pdfMakeInstance = pdfMake;
    return pdfMake;
};

/**
 * Options for generating a QR code PDF
 */
export interface QrPdfOptions {
    /** URL to encode in the QR code */
    url: string;
    /** Building name to display */
    buildingName: string;
    /** Building address to display (optional) */
    buildingAddress?: string;
    /** Logo as data URL (optional) */
    logoDataUrl?: string;
}

/**
 * Fetch an image URL and convert to data URL
 * Needed because pdfmake requires data URLs for images
 */
export const urlToDataUrl = async (url: string): Promise<string> => {
    const response = await fetch(url);

    // Check if fetch was successful
    if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }

    // Verify content-type is an image
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.startsWith('image/')) {
        throw new Error(`Invalid content type: ${contentType}`);
    }

    const blob = await response.blob();

    // Verify blob is an image type
    if (!blob.type.startsWith('image/')) {
        throw new Error(`Blob is not an image: ${blob.type}`);
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            // Verify result is a valid image data URL
            if (!result.startsWith('data:image/')) {
                reject(new Error(`FileReader did not produce image data URL: ${result.substring(0, 50)}`));
                return;
            }
            resolve(result);
        };
        reader.onerror = () => reject(new Error('FileReader failed'));
        reader.readAsDataURL(blob);
    });
};

/**
 * Validate that a string is a valid image data URL for pdfmake
 * pdfmake supports PNG, JPEG formats. WebP may not be supported.
 */
const isValidImageDataUrl = (value: string | undefined): value is string => {
    if (typeof value !== 'string') return false;
    // Must start with data:image/ and have base64 encoding
    // Note: pdfmake only supports PNG and JPEG, NOT webp or svg
    const dataUrlRegex = /^data:image\/(png|jpeg|jpg);base64,/i;
    if (!dataUrlRegex.test(value)) {
        console.warn('Logo data URL does not match expected format (png/jpeg only):', value.substring(0, 80));
        return false;
    }
    // Must have actual base64 content after the header
    const base64Part = value.split(',')[1];
    if (!base64Part || base64Part.length < 100) {
        console.warn('Logo data URL appears to have insufficient data');
        return false;
    }
    return true;
};

/**
 * Re-encode an image data URL through canvas to ensure clean PNG format
 * This fixes issues where pdfmake can't parse certain data URL encodings
 */
export const reencodeImageAsCleanPng = (dataUrl: string): Promise<string> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Could not get canvas context'));
                    return;
                }
                ctx.drawImage(img, 0, 0);
                const cleanDataUrl = canvas.toDataURL('image/png');
                resolve(cleanDataUrl);
            } catch (err) {
                reject(err);
            }
        };
        img.onerror = () => reject(new Error('Failed to load image for re-encoding'));
        img.src = dataUrl;
    });
};

/**
 * Generate a PDF document definition for a QR code poster
 */
const createDocumentDefinition = (options: QrPdfOptions): TDocumentDefinitions => {
    const { url, buildingName, buildingAddress, logoDataUrl } = options;

    const content: Content[] = [];

    // Add logo if provided and valid
    const hasValidLogo = isValidImageDataUrl(logoDataUrl);
    if (hasValidLogo) {
        content.push({
            image: logoDataUrl,
            width: 150,
            alignment: 'center',
            margin: [0, 0, 0, 30],
        });
    }

    // Title
    content.push({
        text: 'Felanmälan',
        style: 'title',
        alignment: 'center',
        margin: [0, hasValidLogo ? 0 : 40, 0, 10],
    });

    // Subtitle
    content.push({
        text: 'Skanna QR-koden för att rapportera ett fel',
        style: 'subtitle',
        alignment: 'center',
        margin: [0, 0, 0, 40],
    });

    // QR Code
    content.push({
        qr: url,
        fit: 250,
        alignment: 'center',
        margin: [0, 0, 0, 40],
    });

    // Building info
    content.push({
        text: buildingName,
        style: 'buildingName',
        alignment: 'center',
        margin: [0, 0, 0, 5],
    });

    if (buildingAddress) {
        content.push({
            text: buildingAddress,
            style: 'buildingAddress',
            alignment: 'center',
            margin: [0, 0, 0, 30],
        });
    }

    // Instructions
    content.push({
        text: [
            '1. Skanna QR-koden med din telefon\n',
            '2. Beskriv problemet\n',
            '3. Ta gärna ett foto\n',
            '4. Skicka in',
        ],
        style: 'instructions',
        alignment: 'center',
        margin: [0, 20, 0, 0],
    });

    // URL text at bottom
    content.push({
        text: url,
        style: 'url',
        alignment: 'center',
        margin: [0, 40, 0, 0],
    });

    return {
        pageSize: 'A4',
        pageMargins: [40, 40, 40, 40],
        content,
        styles: {
            title: {
                fontSize: 32,
                bold: true,
            },
            subtitle: {
                fontSize: 16,
                color: '#666666',
            },
            buildingName: {
                fontSize: 20,
                bold: true,
            },
            buildingAddress: {
                fontSize: 14,
                color: '#666666',
            },
            instructions: {
                fontSize: 14,
                color: '#333333',
                lineHeight: 1.5,
            },
            url: {
                fontSize: 10,
                color: '#999999',
            },
        },
    };
};

/**
 * Generate and download a QR code PDF
 * P1-3: Loads pdfmake dynamically on first use
 */
export const downloadQrPdf = async (options: QrPdfOptions): Promise<void> => {
    const pdfMake = await getPdfMake();
    const docDefinition = createDocumentDefinition(options);
    const fileName = `qr-felanmalan-${options.buildingName.toLowerCase().replace(/\s+/g, '-')}.pdf`;
    pdfMake.createPdf(docDefinition).download(fileName);
};

/**
 * Generate and open a QR code PDF in a new tab
 * P1-3: Loads pdfmake dynamically on first use
 */
export const openQrPdf = async (options: QrPdfOptions): Promise<void> => {
    const pdfMake = await getPdfMake();
    const docDefinition = createDocumentDefinition(options);
    pdfMake.createPdf(docDefinition).open();
};
